import {
  forwardRef,
  isValidElement,
  useState,
  useCallback,
  ReactNode,
  FC,
} from 'react'
import {
  Card,
  IconButton,
  Collapse,
  Paper,
  Stack,
  Alert,
  AlertColor,
  Divider,
  Box,
  Typography,
  TypographyProps,
  AlertProps,
} from '@mui/material'
import { CustomContentProps, useSnackbar, SnackbarContent } from 'notistack'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { alertDetails } from '$constants'

type LoadedSnackbarProps = {
  details?: ReactNode[]
  severity?: AlertColor
  fullWidth?: boolean
  allowDismiss?: boolean
  errorId?: string
  errorCode?: number
  messageTypographyProps?: TypographyProps
  alertProps?: AlertProps
  onDismiss?: () => void
} & Partial<CustomContentProps>

const LoadedSnackbar = forwardRef<HTMLDivElement, LoadedSnackbarProps>(
  (
    {
      id,
      message,
      details = [],
      errorId,
      errorCode,
      severity = 'info',
      fullWidth = false,
      allowDismiss = true,
      messageTypographyProps,
      alertProps,
      onDismiss,
    },
    ref
  ) => {
    const { closeSnackbar } = useSnackbar()
    const [expanded, setExpanded] = useState(false)

    const isCollapsable = errorId || errorCode || details.length > 0

    const handleExpandClick = useCallback(() => {
      setExpanded((prevExpanded) => !prevExpanded)
    }, [])

    const handleDismiss = useCallback(() => {
      // A caller may supply their own callback to be notified of dismissal. We
      // still process the notistack dismissal too though.
      if (onDismiss) {
        onDismiss()
      }
      closeSnackbar(id)
    }, [id, closeSnackbar, onDismiss])

    const messageContent = isValidElement(message) ? (
      message
    ) : (
      <Typography {...messageTypographyProps}>{message}</Typography>
    )

    return (
      <SnackbarContent ref={ref}>
        <Card
          sx={{
            padding: 0,
            ...(fullWidth && { width: '100%' }),
            displayPrint: 'none',
          }}
        >
          <Alert
            severity={severity}
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              borderRadius: 0,
              '.MuiAlert-message': {
                width: '100%',
              },
              ...alertProps?.sx,
            }}
            {...alertProps}
          >
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              {messageContent}
              <Stack
                direction={'row'}
                spacing={'.5rem'}
                sx={{ marginLeft: '1rem !important', alignItems: 'center' }}
              >
                <IconButton
                  aria-label="Show more"
                  size="small"
                  onClick={handleExpandClick}
                  sx={{ padding: 0 }}
                >
                  {isCollapsable &&
                    (expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                </IconButton>
                {allowDismiss && (
                  <IconButton
                    size="small"
                    onClick={handleDismiss}
                    sx={{ padding: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          </Alert>
          {isCollapsable && (
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Divider />
              <Paper
                sx={{
                  paddingY: '1rem',
                  backgroundColor: alertDetails,
                  borderRadius: 0,
                }}
              >
                <Stack direction={'column'} spacing={1}>
                  {errorId && (
                    <MonospaceTypography>ID: {errorId}</MonospaceTypography>
                  )}
                  {errorCode && (
                    <MonospaceTypography>Code: {errorCode}</MonospaceTypography>
                  )}
                  {details.length > 0 && (
                    <>
                      <MonospaceTypography>Messages</MonospaceTypography>
                      {/* We can use index as key here because details is not something that is going to sorted or re rendered */}
                      {details.map((detail, index) => (
                        <MonospaceTypography key={index}>
                          - {detail}
                        </MonospaceTypography>
                      ))}
                    </>
                  )}
                </Stack>
              </Paper>
            </Collapse>
          )}
        </Card>
      </SnackbarContent>
    )
  }
)

LoadedSnackbar.displayName = 'LoadedSnackbar'

export default LoadedSnackbar

/**
 * This component only intends to avoid setting font family on every single typography in the LoadedSnackbar.
 * It is not intended to be a generic component as we should not change the font-family of the components and
 * that is why it lives inside this file.
 */
const MonospaceTypography: FC<{ children: ReactNode }> = (props) => {
  return <Box fontFamily={'monospace'}>{props.children}</Box>
}
